import { Badge } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { CustomerTile } from "@/components/molecules/customer-tile";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { lastActivityAt, type SessionListItem } from "@/features/sessions";
import { shownTags } from "@/features/sessions/classification";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ChannelIcon,
  formatDuration,
  ResultIcon,
  TagBadge,
} from "./conversation-meta";

/**
 * `hideCustomer` for a list that is already one customer's (the contact
 * file's calls tab), where the column only repeats the page's title.
 *
 * The metadata a row is not read for — channel, agent, outcome — is drawn as
 * glyphs named by their tooltips, so the title and the classification keep the
 * width. The number a session came from is not a column: the customer tile
 * already shows it. Nor is a "Voir" button: the row is the link
 * (`CallsTable`'s `onRowNavigate`).
 */
export const useCallsColumns = ({
  hideCustomer = false,
}: { hideCustomer?: boolean } = {}) => {
  const { t } = useTranslation("calls");
  const { format, short } = useDateFormat();
  const { data: agents = [] } = useAgents();

  const columns: ColumnDef<SessionListItem>[] = [
    {
      accessorKey: "type",
      header: t("table.type"),
      cell: ({ row }) => {
        const agent = agents.find((a) => a.id === row.original.agent_id);
        return (
          <div className="flex items-center gap-1.5">
            <ChannelIcon session={row.original} />
            {agent && (
              <Tooltip content={agent.name}>
                <span role="img" aria-label={agent.name}>
                  <AgentAvatar
                    className="size-6"
                    avatarId={agent.avatar_id}
                    name={agent.name}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "customer_id",
      header: t("table.customer"),
      cell: ({ row }) => {
        const customerId = row.original.customer_id;
        return customerId ? (
          <CustomerTile
            customerId={customerId}
            fallback={t("list.unknownCustomer")}
            variant="compact"
            clickable
          />
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: "name",
      header: t("table.name"),
      cell: ({ row }) => {
        const session = row.original;
        const displayName =
          session.name || (session.type === "outbound" ? "Outbound" : "-");
        const displaySummary =
          session.summary ||
          (session.type === "outbound" ? session.purpose : "-");

        return (
          <div className="flex flex-col gap-1 max-w-[200px]">
            <Tooltip content={displayName}>
              <div className="font-medium line-clamp-1">{displayName}</div>
            </Tooltip>
            <Tooltip content={displaySummary}>
              <div className="text-xs text-muted-foreground line-clamp-1">
                {displaySummary}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      accessorKey: "tags",
      header: t("table.classification"),
      cell: ({ row }) => {
        const tags = shownTags(row.original.tags);
        if (tags.length === 0)
          return <span className="text-muted-foreground">-</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 2).map((tag) => (
              <TagBadge key={tag} tag={tag} className="whitespace-nowrap" />
            ))}
            {tags.length > 2 && (
              <Badge variant="secondary">
                +{tags.length - 2}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "state",
      header: t("table.status"),
      cell: ({ row }) => <ResultIcon session={row.original} />,
    },
    {
      accessorKey: "created_at",
      header: t("table.date"),
      cell: ({ row }) => {
        // The duration rides under the date rather than in a column of its
        // own: the row is read for who and why, these two are context.
        const date = lastActivityAt(row.original);
        const duration = row.original.duration || 0;
        return (
          <Tooltip content={format(date, "PPpp")}>
            <div className="whitespace-nowrap tabular-nums">
              <div className="text-sm">{short(date)}</div>
              {duration > 0 && (
                <div className="text-xs text-muted-foreground">
                  {formatDuration(duration / 1000)}
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  return hideCustomer
    ? columns.filter(
        (column) =>
          !("accessorKey" in column && column.accessorKey === "customer_id"),
      )
    : columns;
};
